import React, { useEffect, useState } from 'react';
import api from '../services/api';
import '../styles/Withdrawals.css';

interface WithdrawalRequest {
  _id: string;
  driver: {
    _id: string;
    firstName: string;
    lastName: string;
    phone: string;
  };
  amount: number;
  status: 'pending' | 'approved' | 'rejected' | 'processing' | 'completed';
  bankDetails: {
    accountNumber: string;
    ifscCode: string;
    accountHolderName: string;
    upiId?: string;
  };
  adminNotes?: string;
  processedBy?: {
    firstName: string;
    lastName: string;
  };
  processedAt?: string;
  paymentReference?: string;
  paymentMethod?: string;
  completedBy?: {
    firstName: string;
    lastName: string;
  };
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

const Withdrawals: React.FC = () => {
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [processingId, setProcessingId] = useState<string | null>(null);

  useEffect(() => {
    fetchWithdrawals();
  }, [filter, page]);

  const fetchWithdrawals = async () => {
    setIsLoading(true);
    try {
      const params: any = { page, limit: 20 };

      if (filter !== 'all') {
        params.status = filter;
      }

      const data = await api.getAllWithdrawals(params);
      setWithdrawals(data.withdrawals || []);
      setTotalPages(data.pagination?.pages || 1);
      setError('');
    } catch (err: any) {
      setError('Failed to load withdrawal requests');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (withdrawalId: string, amount: number, driverName: string) => {
    if (!window.confirm(`Approve withdrawal of ₹${amount.toFixed(2)} for ${driverName}?\n\nThis will deduct the amount from the driver's wallet balance.`)) {
      return;
    }

    setProcessingId(withdrawalId);
    try {
      await api.approveWithdrawal(withdrawalId);
      alert('Withdrawal approved successfully');
      fetchWithdrawals();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to approve withdrawal');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (withdrawalId: string, driverName: string) => {
    const reason = window.prompt(`Reject withdrawal for ${driverName}?\n\nPlease provide a reason:`);

    if (!reason || reason.trim() === '') {
      return;
    }

    setProcessingId(withdrawalId);
    try {
      await api.rejectWithdrawal(withdrawalId, reason.trim());
      alert('Withdrawal rejected');
      fetchWithdrawals();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to reject withdrawal');
    } finally {
      setProcessingId(null);
    }
  };

  const handleComplete = async (withdrawalId: string, amount: number, driverName: string, bankDetails: any) => {
    // Prompt for payment reference
    const paymentReference = window.prompt(
      `Mark withdrawal as COMPLETED for ${driverName}?\n\n` +
      `Amount: ₹${amount.toFixed(2)}\n` +
      `Account: ${bankDetails.accountHolderName} (****${bankDetails.accountNumber.slice(-4)})\n\n` +
      `Enter payment reference/transaction ID:`
    );

    if (!paymentReference || paymentReference.trim() === '') {
      return;
    }

    // Prompt for payment method
    const paymentMethod = window.prompt(
      'Select payment method:\n' +
      '1 - NEFT\n' +
      '2 - RTGS\n' +
      '3 - IMPS\n' +
      '4 - UPI\n' +
      '5 - Other\n\n' +
      'Enter number (1-5):'
    );

    const methods = ['NEFT', 'RTGS', 'IMPS', 'UPI', 'Other'];
    const methodIndex = parseInt(paymentMethod || '') - 1;

    if (isNaN(methodIndex) || methodIndex < 0 || methodIndex > 4) {
      alert('Invalid payment method selection');
      return;
    }

    const selectedMethod = methods[methodIndex];

    // Optional notes
    const notes = window.prompt('Additional notes (optional):') || '';

    setProcessingId(withdrawalId);
    try {
      await api.completeWithdrawal(withdrawalId, paymentReference.trim(), selectedMethod, notes.trim());
      alert('Withdrawal marked as completed successfully!');
      fetchWithdrawals();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to complete withdrawal');
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'status-badge pending';
      case 'processing':
        return 'status-badge processing';
      case 'approved':
        return 'status-badge approved';
      case 'completed':
        return 'status-badge completed';
      case 'rejected':
        return 'status-badge rejected';
      default:
        return 'status-badge';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading && withdrawals.length === 0) {
    return (
      <div className="withdrawals-page">
        <div className="loading">Loading withdrawal requests...</div>
      </div>
    );
  }

  return (
    <div className="withdrawals-page">
      <div className="page-header">
        <div>
          <h1>Withdrawal Management</h1>
          <p>Review and process driver withdrawal requests</p>
        </div>
        <button className="refresh-button" onClick={fetchWithdrawals}>
          🔄 Refresh
        </button>
      </div>

      {error && <div className="error-box">{error}</div>}

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>Status:</label>
          <select value={filter} onChange={(e) => { setFilter(e.target.value); setPage(1); }}>
            <option value="all">All Requests</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="approved">Approved</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className="results-count">
          {withdrawals.length} request{withdrawals.length !== 1 ? 's' : ''} found
        </div>
      </div>

      {/* Desktop Table */}
      <div className="desktop-table-container">
        <div className="table-container">
          <table className="withdrawals-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Driver</th>
              <th>Amount</th>
              <th>Bank Details</th>
              <th>Requested On</th>
              <th>Processed</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {withdrawals.map((withdrawal) => (
              <tr key={withdrawal._id}>
                <td>
                  <span className={getStatusBadgeClass(withdrawal.status)}>
                    {withdrawal.status.toUpperCase()}
                  </span>
                </td>
                <td>
                  <div className="driver-info">
                    <strong>{withdrawal.driver.firstName} {withdrawal.driver.lastName}</strong>
                    <div className="driver-phone">{withdrawal.driver.phone}</div>
                  </div>
                </td>
                <td>
                  <span className="amount">₹{withdrawal.amount.toFixed(2)}</span>
                </td>
                <td>
                  <div className="bank-details">
                    <div className="bank-name">{withdrawal.bankDetails.accountHolderName}</div>
                    <div className="bank-account">****{withdrawal.bankDetails.accountNumber.slice(-4)}</div>
                    <div className="bank-ifsc">{withdrawal.bankDetails.ifscCode}</div>
                    {withdrawal.bankDetails.upiId && (
                      <div className="bank-upi">UPI: {withdrawal.bankDetails.upiId}</div>
                    )}
                  </div>
                </td>
                <td>{formatDate(withdrawal.createdAt)}</td>
                <td>
                  {withdrawal.processedAt ? (
                    <div className="processed-info">
                      <div>{formatDate(withdrawal.processedAt)}</div>
                      {withdrawal.processedBy && (
                        <div className="processed-by">
                          by {withdrawal.processedBy.firstName} {withdrawal.processedBy.lastName}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="not-processed">-</span>
                  )}
                </td>
                <td>
                  {withdrawal.status === 'pending' ? (
                    <div className="action-buttons">
                      <button
                        onClick={() => handleApprove(
                          withdrawal._id,
                          withdrawal.amount,
                          `${withdrawal.driver.firstName} ${withdrawal.driver.lastName}`
                        )}
                        className="btn-approve"
                        disabled={processingId === withdrawal._id}
                      >
                        {processingId === withdrawal._id ? 'Processing...' : '✓ Approve'}
                      </button>
                      <button
                        onClick={() => handleReject(
                          withdrawal._id,
                          `${withdrawal.driver.firstName} ${withdrawal.driver.lastName}`
                        )}
                        className="btn-reject"
                        disabled={processingId === withdrawal._id}
                      >
                        ✗ Reject
                      </button>
                    </div>
                  ) : withdrawal.status === 'approved' ? (
                    <div className="action-buttons">
                      <button
                        onClick={() => handleComplete(
                          withdrawal._id,
                          withdrawal.amount,
                          `${withdrawal.driver.firstName} ${withdrawal.driver.lastName}`,
                          withdrawal.bankDetails
                        )}
                        className="btn-complete"
                        disabled={processingId === withdrawal._id}
                      >
                        {processingId === withdrawal._id ? 'Processing...' : '💸 Mark as Completed'}
                      </button>
                    </div>
                  ) : withdrawal.status === 'completed' && withdrawal.paymentReference ? (
                    <div className="payment-details">
                      <div><strong>Payment Ref:</strong> {withdrawal.paymentReference}</div>
                      <div><strong>Method:</strong> {withdrawal.paymentMethod}</div>
                      {withdrawal.completedAt && (
                        <div className="completed-date">Completed: {formatDate(withdrawal.completedAt)}</div>
                      )}
                    </div>
                  ) : withdrawal.status === 'rejected' && withdrawal.adminNotes ? (
                    <div className="rejection-reason">
                      <strong>Reason:</strong>
                      <div>{withdrawal.adminNotes}</div>
                    </div>
                  ) : (
                    <span className="no-actions">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

          {withdrawals.length === 0 && !isLoading && (
            <div className="empty-state">
              <p>No withdrawal requests found</p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-card-list">
        {withdrawals.map((withdrawal) => (
          <div key={withdrawal._id} className="data-card">
            <div className="data-card-header">
              <div>
                <div className="data-card-title">
                  {withdrawal.driver.firstName} {withdrawal.driver.lastName}
                </div>
                <div className="data-card-subtitle">{withdrawal.driver.phone}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span className={getStatusBadgeClass(withdrawal.status)}>
                  {withdrawal.status.toUpperCase()}
                </span>
                <div className="amount" style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '4px' }}>
                  ₹{withdrawal.amount.toFixed(2)}
                </div>
              </div>
            </div>

            <div className="data-card-body">
              <div className="data-card-row">
                <span className="data-card-label">Account Holder:</span>
                <span className="data-card-value">{withdrawal.bankDetails.accountHolderName}</span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Account:</span>
                <span className="data-card-value">****{withdrawal.bankDetails.accountNumber.slice(-4)}</span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">IFSC:</span>
                <span className="data-card-value">{withdrawal.bankDetails.ifscCode}</span>
              </div>
              {withdrawal.bankDetails.upiId && (
                <div className="data-card-row">
                  <span className="data-card-label">UPI:</span>
                  <span className="data-card-value">{withdrawal.bankDetails.upiId}</span>
                </div>
              )}
              <div className="data-card-row">
                <span className="data-card-label">Requested:</span>
                <span className="data-card-value">{formatDate(withdrawal.createdAt)}</span>
              </div>
              {withdrawal.processedAt && (
                <div className="data-card-row">
                  <span className="data-card-label">Processed:</span>
                  <span className="data-card-value">
                    {formatDate(withdrawal.processedAt)}
                    {withdrawal.processedBy && (
                      <div style={{ fontSize: '12px', color: '#718096' }}>
                        by {withdrawal.processedBy.firstName} {withdrawal.processedBy.lastName}
                      </div>
                    )}
                  </span>
                </div>
              )}
            </div>

            <div className="data-card-actions">
              {withdrawal.status === 'pending' ? (
                <>
                  <button
                    onClick={() => handleApprove(
                      withdrawal._id,
                      withdrawal.amount,
                      `${withdrawal.driver.firstName} ${withdrawal.driver.lastName}`
                    )}
                    className="btn-approve"
                    disabled={processingId === withdrawal._id}
                  >
                    {processingId === withdrawal._id ? 'Processing...' : '✓ Approve'}
                  </button>
                  <button
                    onClick={() => handleReject(
                      withdrawal._id,
                      `${withdrawal.driver.firstName} ${withdrawal.driver.lastName}`
                    )}
                    className="btn-reject"
                    disabled={processingId === withdrawal._id}
                  >
                    ✗ Reject
                  </button>
                </>
              ) : withdrawal.status === 'approved' ? (
                <button
                  onClick={() => handleComplete(
                    withdrawal._id,
                    withdrawal.amount,
                    `${withdrawal.driver.firstName} ${withdrawal.driver.lastName}`,
                    withdrawal.bankDetails
                  )}
                  className="btn-complete"
                  disabled={processingId === withdrawal._id}
                >
                  {processingId === withdrawal._id ? 'Processing...' : '💸 Mark as Completed'}
                </button>
              ) : withdrawal.status === 'completed' && withdrawal.paymentReference ? (
                <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                  <div><strong>Payment Ref:</strong> {withdrawal.paymentReference}</div>
                  <div><strong>Method:</strong> {withdrawal.paymentMethod}</div>
                  {withdrawal.completedAt && (
                    <div style={{ color: '#718096', marginTop: '4px' }}>
                      Completed: {formatDate(withdrawal.completedAt)}
                    </div>
                  )}
                </div>
              ) : withdrawal.status === 'rejected' && withdrawal.adminNotes ? (
                <div style={{ fontSize: '13px', color: '#c53030' }}>
                  <strong>Reason:</strong> {withdrawal.adminNotes}
                </div>
              ) : null}
            </div>
          </div>
        ))}

        {withdrawals.length === 0 && !isLoading && (
          <div className="empty-state">
            <p>No withdrawal requests found</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Withdrawals;
